/**
 * Autosave Status Component
 *
 * Shows the current autosave status in the workflow sub-header.
 * Styled to match LogicAppsUX Designer V2 pattern.
 *
 * Displays relative time like "Autosaved a few seconds ago" that updates
 * every 10 seconds to keep the display current. When the user has turned
 * autosave off, the label switches to "Saved …" (manual save) and a
 * prominent "Unsaved changes" hint is shown while edits are pending.
 *
 * Reads from `DraftModeContext` when available and falls back to
 * `useDraftModeStore` selectors otherwise so the component can be hosted
 * above `DraftModeProvider` in the tree (same pattern as `DraftBadge`).
 */

import { Text, Spinner, Tooltip, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { ErrorCircle16Regular, CheckmarkCircle16Regular } from '@fluentui/react-icons';
import { useDraftModeOptional } from '@/contexts/DraftModeContext';
import {
  useDraftModeStore,
  selectIsDraftMode,
  selectDraftStatus,
  selectLastSavedTime,
  selectSaveError,
} from '@/stores/draftModeStore';
import { useUIPreferencesStore } from '@/stores/uiPreferencesStore';
import { useIsDraftDirty } from '@/hooks/useIsDraftDirty';
import { formatDateTime, useRelativeTime } from '@/utils/dateTime';
import type { DraftStatus } from '@/types/draftMode';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  text: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    whiteSpace: 'nowrap',
    fontStyle: 'italic',
  },
  savingText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap',
  },
  unsavedText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteDarkOrangeForeground1,
    whiteSpace: 'nowrap',
    fontStyle: 'normal',
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    fontStyle: 'normal',
  },
  errorIcon: {
    color: tokens.colorPaletteRedForeground1,
  },
  successIcon: {
    color: tokens.colorPaletteGreenForeground1,
  },
});

export interface AutosaveStatusProps {
  /** Override for autosave status (uses context if not provided) */
  isAutoSaving?: boolean;
  /** Override for last saved time (uses context if not provided) */
  lastSavedTime?: Date | null;
  /** Override for save error (uses context if not provided) */
  saveError?: string | null;
  /** Override for draft status (uses context if not provided) */
  draftStatus?: DraftStatus;
  /** Show checkmark icon when saved (default: false) */
  showSuccessIcon?: boolean;
}

export const AutosaveStatus: React.FC<AutosaveStatusProps> = ({
  isAutoSaving: isAutoSavingOverride,
  lastSavedTime: lastSavedTimeOverride,
  saveError: saveErrorOverride,
  draftStatus: draftStatusOverride,
  showSuccessIcon = false,
}) => {
  const styles = useStyles();
  const draftMode = useDraftModeOptional();

  // Store fallbacks — `AutosaveStatus` may be rendered above `DraftModeProvider`
  // (e.g. in the workflow sub-header). The hook calls themselves are
  // unconditional so we don't violate the Rules of Hooks.
  const storeIsDraftMode = useDraftModeStore(selectIsDraftMode);
  const storeDraftStatus = useDraftModeStore(selectDraftStatus);
  const storeLastSavedTime = useDraftModeStore(selectLastSavedTime);
  const storeSaveError = useDraftModeStore(selectSaveError);
  const isDirty = useIsDraftDirty();
  const draftAutosaveMode = useUIPreferencesStore((s) => s.draftAutosaveMode);

  // Use overrides if provided, otherwise prefer context, fall back to store.
  // These need to be computed before hooks to maintain hook order.
  const isAutoSaving =
    isAutoSavingOverride ?? draftMode?.isAutoSaving ?? storeDraftStatus === 'saving';
  const lastSavedTime = lastSavedTimeOverride ?? draftMode?.lastSavedTime ?? storeLastSavedTime;
  const saveError = saveErrorOverride ?? draftMode?.saveError ?? storeSaveError;
  const draftStatus = draftStatusOverride ?? draftMode?.draftStatus ?? storeDraftStatus;
  const isDraftMode = draftMode?.isDraftMode ?? storeIsDraftMode;
  const isAutosaveOff = draftAutosaveMode === 'off';

  // Self-ticking relative-time label. Re-renders every 10s while
  // `lastSavedTime` is non-null and tears down on unmount / when it
  // becomes null. Must run unconditionally (before early returns) to keep
  // hook order stable.
  const relativeTime = useRelativeTime(lastSavedTime, { style: 'phrase' });

  // Don't show status outside draft mode — the Published view has nothing to autosave.
  if (!isDraftMode) {
    return null;
  }

  // Show error state
  if (saveError) {
    return (
      <Tooltip content={saveError} relationship="description">
        <div className={styles.container}>
          <ErrorCircle16Regular className={styles.errorIcon} />
          <Text className={mergeClasses(styles.text, styles.errorText)}>
            Error saving
          </Text>
        </div>
      </Tooltip>
    );
  }

  // Show saving state
  //
  // In manual-save mode the dedicated `ManualSaveButton` already shows its
  // own spinner while a save is in flight; rendering a second "Saving…"
  // pill here would be redundant. Suppress this branch when autosave is
  // off and fall through to the dirty / last-saved branches below so the
  // header still reflects pending changes via "Unsaved changes" or the
  // post-save "Saved …" label.
  if ((isAutoSaving || draftStatus === 'saving') && !isAutosaveOff) {
    return (
      <div className={styles.container}>
        <Spinner size="tiny" />
        <Text className={styles.savingText}>Saving...</Text>
      </div>
    );
  }

  // Manual-save mode + dirty: surface the pending-changes hint prominently
  // because no automatic write is coming. In throttled modes we still rely
  // on the relative-time label below — the throttled save will catch up.
  if (isAutosaveOff && isDirty) {
    return (
      <Tooltip content="Autosave is off. Click Save to persist changes." relationship="description">
        <div className={styles.container}>
          <Text className={styles.unsavedText}>Unsaved changes</Text>
        </div>
      </Tooltip>
    );
  }

  // Show last saved time with relative format
  if (lastSavedTime) {
    // Full locale string (e.g. "5/20/2026, 11:31:15 AM") for the tooltip —
    // gives users an exact timestamp on hover while keeping the visible
    // label compact and relative.
    const absoluteTime = formatDateTime(lastSavedTime, 'locale');
    // When the user has disabled autosave, the timestamp reflects an
    // explicit manual save — labeling it "Autosaved" would be misleading.
    const label = isAutosaveOff ? `Saved ${relativeTime}` : `Autosaved ${relativeTime}`;
    const tooltipPrefix = isAutosaveOff ? 'Draft saved at' : 'Draft autosaved at';

    return (
      <Tooltip content={`${tooltipPrefix}: ${absoluteTime}`} relationship="description">
        <div className={styles.container}>
          {showSuccessIcon && <CheckmarkCircle16Regular className={styles.successIcon} />}
          <Text className={styles.text}>{label}</Text>
        </div>
      </Tooltip>
    );
  }

  // Show dirty state (unsaved changes) — used in throttled modes between
  // the first edit and the first autosave landing.
  if (draftStatus === 'dirty' || isDirty) {
    return (
      <div className={styles.container}>
        <Text className={styles.savingText}>Unsaved changes</Text>
      </div>
    );
  }

  // Default: nothing to show
  return null;
};

export default AutosaveStatus;
